import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import {
  Select,
  MenuItem,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useSnackbar } from "notistack";
import { ItemType, ResProducts } from "./Components/interface";
import SouthEastSharpIcon from "@mui/icons-material/SouthEastSharp";
import NorthEastSharpIcon from "@mui/icons-material/NorthEastSharp";
import { useUrlQueryParams, useDebounce, useHttp } from "./Hooks";
import { searchParamsAPI } from "./Components/config";
import { Currency } from "./Components/Curreny";
import { MarketAgentComponent } from "./Components/MarketAgent";
import { RiskAgentComponent } from "./Components/RiskAgent";
import { WhaleAgentComponent } from "./Components/whaleAgent";
import {
  formatDescription,
  formatNumber,
  formatCurrency,
  checkTrend,
  getTrendProcent,
} from "./Components/utils/format-utils";
import { apiUrl } from "./Components/config";
import { Navigation, LoadingPage } from "./Pages";
import { Tooltip as ReactTooltip } from "react-tooltip";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useAIChat } from "./Hooks";
import { PriceData } from "./Components/interface";
import { useAuth } from "./Auth";
import ReactMarkdown from "react-markdown";

export const App = () => {
  // for input field
  const { withAuth } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dollar, setDollar] = useState(0);
  const [displayDollar, setDisplayDollar] = useState(false);
  const [beraDollar, setBeraDollar] = useState(0);
  const [displayBeraDollar, setDisplayBeraDollar] = useState(false);
  // set selected collection details
  const [selectedItem, setSelectedItem] = useState<ItemType | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [visibleCount, setVisibleCount] = useState(30); // 初始加载 30 个
  const [isFetching, setIsFetching] = useState(false); // 控制是否正在加载

  const [searchParams, setSearchParams] = useUrlQueryParams(searchParamsAPI);
  const debouncedParam = useDebounce(searchParams, 500);
  const client = useHttp();
  const {
    data: apiData,
    isLoading,
    isError,
    status,
  } = useQuery<ResProducts, string>(["products", debouncedParam], () => {
    return client("list", { url: apiUrl, data: debouncedParam });
  });
  const {
    multiAgents,
    sendMessage,
    handleConnect,
    disconnect,
    approve,
    reject,
    toggleCollapse,
    resetMultiAgents,
  } = useAIChat();

  // 处理滚动事件
  const handleScroll = () => {
    if (
      window.innerHeight + document.documentElement.scrollTop + 100 >=
        document.documentElement.offsetHeight &&
      !isFetching
    ) {
      setIsFetching(true);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isFetching]);

  useEffect(() => {
    if (!isFetching) return;
    const loadMore = () => {
      setVisibleCount((prevCount) => prevCount + 6); // 每次加载 6 个
      setIsFetching(false);
    };
    loadMore();
  }, [isFetching]);

  const filteredData = searchTerm
    ? apiData?.payload.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.collection.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.contracts_address.includes(searchTerm.toLowerCase()),
      )
    : apiData?.payload;

  const fetchItemDetails = (name: string) => {
    //setIsLoading(true);
    try {
      const item = filteredData?.find((item) => item.collection === name);
      if (item) {
        setSelectedItem(item);
        handleLinkClick(item.collection, "item", "");
        handleConnect();
        console.log("item", item);
        setDialogOpen(true);
        sendMessage(item);
        console.log("AI Chat initialized with greeting message.");
      }
    } catch (error) {
      console.error("get data error:", error);
    } finally {
      //setIsLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          handleClickSnackbar("contracts address is already in your Clipboard");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
        });
    } else {
      fallbackCopyTextToClipboard(text);
    }
  };

  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      handleClickSnackbar("contracts address is already in your Clipboard");
    } catch (err) {
      console.error("Fallback: Oops, unable to copy", err);
    }
    document.body.removeChild(textArea);
  };

  const handleClickSnackbar = (message: string) => {
    enqueueSnackbar(message, {
      variant: "success",
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
  };

  const handleSearch = (e: any) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    if (event.target.value === "mc") {
      navigate("/universe");
    } else {
      setSearchParams({ ["sort"]: event.target.value });
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setSearchParams({ ["category"]: event.target.value });
  };

  const handleChainsChange = (event: SelectChangeEvent) => {
    setSearchParams({ ["chains"]: event.target.value });
  };

  const handleMarketChange = (event: SelectChangeEvent) => {
    setSearchParams({ ["marketplace"]: event.target.value });
  };

  const handleLinkClick = async (name: string, type: string, url: string) => {
    if (url !== "") {
      window.open(url, "_blank");
      traceLink(name, type, url);
    }
  };

  const traceLink = async (name: string, type: string, url: string) => {
    try {
      if (name !== "" && type !== "") {
        await fetch("/v1/api/fetchAttr", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name, type }),
        });
      }
    } catch (error) {
      console.error("click error:", error);
    }
  };

  const changeCurrency = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setDisplayDollar(isChecked);

    if (displayDollar === false) {
      const response = await fetch("/v1/api/currency?symbol=ETH", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch price data");
      }

      const data: PriceData = await response.json();
      setDollar(data.price);
    }
  };

  const changeBeraCurrency = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked;
    setDisplayBeraDollar(isChecked);

    if (displayBeraDollar === false) {
      const response = await fetch("/v1/api/currency?symbol=BERA", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch price data");
      }

      const data: PriceData = await response.json();
      setBeraDollar(data.price);
    }
  };

  const getPriceWithCurrency = (floorprice: number, symbol: string) => {
    if (displayDollar && symbol.toUpperCase() == "ETH") {
      return "$" + formatNumber(floorprice * dollar, true) + " ";
    } else if (displayBeraDollar && symbol.toUpperCase() == "BERA") {
      return "$" + formatNumber(floorprice * beraDollar, true) + " ";
    } else {
      return (
        formatNumber(Math.round(floorprice * 10000) / 10000) +
        " " +
        symbol +
        " "
      );
    }
  };

  const StringToHtml = (
    inputString: string,
    type: "market" | "risk" | "whale",
  ) => {
    // Use the delimiter to split the string into an array of lines
    const stringArray = inputString
      .split("[@@@]")
      .map((item) => item.trim())
      .filter((item) => item !== "");
    const colors = {
      market: "#5ad7f5",
      risk: "#ffb38a",
      whale: "#c4b5fd",
    };

    const completedColors = {
      market: "rgba(90,215,245,0.42)",
      risk: "rgba(255,179,138,0.42)",
      whale: "rgba(196,181,253,0.42)",
    };

    // Map the array to a list of div elements
    const outputHtml = stringArray.map((item, index) => {
      // Determine the class name based on whether it's the last item
      const isLastItem = index === stringArray.length - 1;
      const className = isLastItem ? colors[type] : completedColors[type];
      // Create the div element with the specified content and class name
      return (
        <div key={index} style={{ color: `${className}` }}>
          <span>
            <i
              style={{ color: `${className}` }}
              className="fas fa-angle-right pr-[3px]"
            ></i>
            {item}
          </span>
          {isLastItem ? <span className={`typing-cursor-${type}`}></span> : ""}
        </div>
      );
    });

    // Render the list of div elements
    return <div>{outputHtml}</div>;
  };

  const handleClose = () => {
    disconnect();
    resetMultiAgents();
    setDialogOpen(false);
  };

  return (
    <>
      {isLoading && <LoadingPage text="Loading NFTs Search Engines" />}
      <div
        className={`page-content ${!isLoading ? "loaded left-nav-sidebar" : ""}`}
      >
        <Navigation name="" />
        <section className="local-no-padding md-padding-80px-top xs-padding-80px-top sm-padding-80px-top">
          <div className="currency">
            <FormGroup>
              <FormControlLabel
                className="bera"
                control={
                  <Currency
                    sx={{ m: 1 }}
                    checked={displayBeraDollar}
                    onChange={changeBeraCurrency}
                  />
                }
                label={
                  displayBeraDollar
                    ? formatCurrency(beraDollar, "USD", false, true)
                    : "BERA"
                }
              />
              <FormControlLabel
                control={
                  <Currency
                    sx={{ m: 1 }}
                    checked={displayDollar}
                    onChange={changeCurrency}
                  />
                }
                label={
                  displayDollar ? formatCurrency(dollar, "USD", true) : "ETH"
                }
              />
            </FormGroup>
          </div>
          <div className="container">
            <div className="row">
              <div className="top-filter col-12 flex align-items-center justify-content-between px-[15px]">
                <div className={"filter flex gap-[12px]"}>
                  <Select
                    value={searchParams?.marketplace}
                    onChange={handleMarketChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                  >
                    <MenuItem value={""}>All Marketplaces</MenuItem>
                    <MenuItem value={"opensea"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <img width={25} height={25} src="/images/opensea.png" />
                        <Typography style={{ fontSize: "12px" }}>
                          Opensea
                        </Typography>
                      </div>
                    </MenuItem>
                    <MenuItem value={"kingdomly"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <img
                          width={25}
                          height={25}
                          src="/images/kingdomly.png"
                        />
                        <Typography style={{ fontSize: "12px" }}>
                          Kingdomly
                        </Typography>
                      </div>
                    </MenuItem>
                  </Select>
                  <Select
                    value={searchParams?.chains}
                    onChange={handleChainsChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                  >
                    <MenuItem value={""}>All Chains</MenuItem>
                    <MenuItem value={"arbitrum"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <img
                          width={25}
                          height={25}
                          src="/images/arbitrum.png"
                        />
                        <Typography style={{ fontSize: "12px" }}>
                          Arbitrum
                        </Typography>
                      </div>
                    </MenuItem>
                    <MenuItem value={"berachain"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <img
                          width={25}
                          height={25}
                          src="/images/berachain.png"
                        />
                        <Typography style={{ fontSize: "12px" }}>
                          Berachain
                        </Typography>
                      </div>
                    </MenuItem>
                    <MenuItem value={"ethereum"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <img
                          width={25}
                          height={25}
                          src="/images/ethereum.png"
                        />
                        <Typography style={{ fontSize: "12px" }}>
                          Ethereum
                        </Typography>
                      </div>
                    </MenuItem>
                    <MenuItem value={"zora"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <img
                          width={25}
                          height={25}
                          src="/images/chains/zora.png"
                        />
                        <Typography style={{ fontSize: "12px" }}>
                          Zora
                        </Typography>
                      </div>
                    </MenuItem>
                    <MenuItem value={"optimism"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <img
                          width={25}
                          height={25}
                          src="/images/chains/optimism.png"
                        />
                        <Typography style={{ fontSize: "12px" }}>
                          Optimsism
                        </Typography>
                      </div>
                    </MenuItem>
                    <MenuItem value={"base"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <img
                          width={25}
                          height={25}
                          src="/images/chains/base.png"
                        />
                        <Typography style={{ fontSize: "12px" }}>
                          Base
                        </Typography>
                      </div>
                    </MenuItem>
                  </Select>
                  <Select
                    value={searchParams?.category}
                    onChange={handleCategoryChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                  >
                    <MenuItem value={""}>All Categories</MenuItem>
                    <MenuItem value={"art"}>ART</MenuItem>
                    <MenuItem value={"gaming"}>GAMING</MenuItem>
                    <MenuItem value={"memberships"}>MEMBERSHIPS</MenuItem>
                    <MenuItem value={"pfps"}>PFPS</MenuItem>
                    <MenuItem value={"virtual-worlds"}>VIRTUAL-WORLDS</MenuItem>
                    <MenuItem value={"others"}>OTHERS</MenuItem>
                  </Select>
                  <Select
                    value={searchParams?.sort}
                    onChange={handleSortChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                  >
                    <MenuItem value="">Sort By Default</MenuItem>
                    <MenuItem value={"floor_price:asc"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography style={{ fontSize: "12px" }}>
                          FLOOR PRICE
                        </Typography>
                        <ArrowDownwardRoundedIcon
                          style={{
                            marginLeft: 8,
                            width: "14px",
                            height: "14px",
                            color: "#2e7d32",
                          }}
                        />
                      </div>
                    </MenuItem>
                    <MenuItem value={"floor_price:desc"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography style={{ fontSize: "12px" }}>
                          FLOOR PRICE
                        </Typography>
                        <ArrowUpwardRoundedIcon
                          style={{
                            marginLeft: 8,
                            width: "14px",
                            height: "14px",
                            color: "#d32f2f",
                          }}
                        />
                      </div>
                    </MenuItem>
                    <MenuItem value={"volume:asc"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography style={{ fontSize: "12px" }}>
                          TOTAL VOLUME
                        </Typography>
                        <ArrowDownwardRoundedIcon
                          style={{
                            marginLeft: 8,
                            width: "14px",
                            height: "14px",
                            color: "#2e7d32",
                          }}
                        />
                      </div>
                    </MenuItem>
                    <MenuItem value={"volume:desc"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography style={{ fontSize: "12px" }}>
                          TOTAL VOLUME
                        </Typography>
                        <ArrowUpwardRoundedIcon
                          style={{
                            marginLeft: 8,
                            width: "14px",
                            height: "14px",
                            color: "#d32f2f",
                          }}
                        />
                      </div>
                    </MenuItem>
                    <MenuItem value={"trend:desc"}>WEEKLY GAIN</MenuItem>
                    <MenuItem value={"trend:asc"}>WEEKLY LOSS</MenuItem>
                    <MenuItem value={"created_date:desc"}>
                      NEWEST FIRST
                    </MenuItem>
                    <MenuItem value={"created_date:asc"}>OLDEST FIRST</MenuItem>
                    <MenuItem value={"mc"}>MARKET CAP</MenuItem>
                  </Select>
                </div>
                <form
                  method="post"
                  className="search-collection navbar-form no-padding search-box"
                >
                  <div className="input-group add-on position-relative">
                    <TextField
                      variant="outlined"
                      placeholder="conllections OR addresses..."
                      value={searchTerm}
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12 filter-content overflow-hidden">
                <div className="hover-option6 grid grid-auto gutter-medium">
                  {filteredData?.slice(0, visibleCount).map((item) => {
                    return (
                      <div
                        key={item.collection}
                        className="grid-item last-paragraph-no-margin"
                      >
                        <figure>
                          <div className="rounded-t-lg portfolio-img bg-white-opacity position-relative text-center overflow-hidden">
                            <img
                              src={
                                "./images/nfts/" +
                                (item.image_url !== ""
                                  ? item.collection
                                  : "no-image") +
                                ".png"
                              }
                              alt={item.collection}
                            />
                            <div
                              className="portfolio-icon flex align-items-center justify-content-center"
                              style={{ top: "75%" }}
                            >
                              {item.project_url !== "" ? (
                                <a
                                  href={"#"}
                                  title={"Project"}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleLinkClick(
                                      item.collection,
                                      "item",
                                      item.project_url,
                                    );
                                  }}
                                >
                                  <i
                                    className="fas fa-link text-extra-dark-gray"
                                    aria-hidden="true"
                                  ></i>
                                </a>
                              ) : (
                                ""
                              )}

                              <a
                                className={"opensea"}
                                href={"#"}
                                title={"OpenSea"}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleLinkClick(
                                    item.collection,
                                    "opensea",
                                    "https://opensea.io/collection/" +
                                      item.collection,
                                  );
                                }}
                              >
                                <img src={"./images/opensea_w.png"} />
                              </a>

                              {item?.twitter_url && (
                                <a
                                  href={"#"}
                                  title={"Twitter"}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleLinkClick(
                                      item.collection,
                                      "twitter",
                                      "https://x.com/" + item.twitter_url,
                                    );
                                  }}
                                >
                                  <i
                                    className="fab fa-twitter text-extra-dark-gray"
                                    aria-hidden="true"
                                  ></i>
                                </a>
                              )}

                              {item?.discord_url && (
                                <a
                                  href={"#"}
                                  title={"Discord"}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleLinkClick(
                                      item.collection,
                                      "discord",
                                      item.discord_url,
                                    );
                                  }}
                                >
                                  <i
                                    className="fab fa-discord text-extra-dark-gray"
                                    aria-hidden="true"
                                  ></i>
                                </a>
                              )}

                              {item?.telegram_url && (
                                <a
                                  href={"#"}
                                  title={"Telegram"}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleLinkClick(
                                      item.collection,
                                      "telegram",
                                      item.telegram_url,
                                    );
                                  }}
                                >
                                  <i
                                    className="fab fa-telegram text-extra-dark-gray"
                                    aria-hidden="true"
                                  ></i>
                                </a>
                              )}
                            </div>
                            <div className="portfolio-icon flex align-items-center justify-content-center">
                              <a
                                className="gallery-link glow-on-hover cursor-pointer"
                                title="ask AI about this collection"
                                onClick={withAuth(() => {
                                  fetchItemDetails(item.collection);
                                })}
                              >
                                <img
                                  className="cursor-pointer"
                                  style={{
                                    position: "relative",
                                    top: "8px",
                                    left: "6px",
                                    color: "black",
                                    opacity: 1,
                                    width: "28px",
                                    height: "28px",
                                  }}
                                  src="./images/chat-bot.png"
                                />
                              </a>
                            </div>
                          </div>
                          <figcaption className="rounded-b-lg bg-white">
                            {item.role > 0 ? (
                              <div
                                title={
                                  "For reference only, see Berachain Discord for more info!"
                                }
                                className={
                                  "flex discord-role position-absolute top-1 left-1"
                                }
                              >
                                <svg viewBox="0 0 30 30" width="30" height="30">
                                  <circle
                                    fill={item.color}
                                    className="circle"
                                    cx="16"
                                    cy="16"
                                    r="12"
                                  />
                                </svg>
                                <img
                                  width={25}
                                  src={
                                    "./images/roles/" +
                                    item.role.toString() +
                                    ".svg"
                                  }
                                />
                              </div>
                            ) : (
                              ""
                            )}
                            {item.contracts_address !== "" ? (
                              <div
                                title={"click to copy contracts address"}
                                className={"portfolio-chain"}
                                onClick={() =>
                                  handleCopy(item.contracts_address)
                                }
                              >
                                <img
                                  src={
                                    "./images/chains/" +
                                    item.contracts_chain +
                                    ".png"
                                  }
                                  alt={item.contracts_chain}
                                />
                              </div>
                            ) : (
                              ""
                            )}
                            <div className="portfolio-hover-main text-center mb-[0px] mt-[-12px]">
                              <div className="portfolio-hover-box align-middle">
                                <div className="portfolio-hover-content position-relative">
                                  <a
                                    title="ask AI about this collection"
                                    onClick={withAuth(() => {
                                      fetchItemDetails(item.collection);
                                    })}
                                    target={"_blank"}
                                  >
                                    <span className="cursor-pointer line-height-normal font-weight-600 text-small margin-5px-bottom text-extra-dark-gray text-uppercase d-block">
                                      {item.name}
                                    </span>
                                  </a>
                                  <p className="description text-medium-gray text-extra-small">
                                    {item.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className={"flex justify-between"}>
                                <div className={"flex flex-col justify-end"}>
                                  <span
                                    className={
                                      checkTrend(item)
                                        ? "text-[11px] font-[800] text-[#2e7d32] leading-[8px]"
                                        : "text-[11px] font-[800] text-[#d32f2f] leading-[8px]"
                                    }
                                  >
                                    {item.floor_price == 0
                                      ? "not list"
                                      : getPriceWithCurrency(
                                          item.floor_price,
                                          item.floor_price_symbol,
                                        )}
                                    <span
                                      data-tooltip-id="procent_tooltip"
                                      className="sm-display-none"
                                    >
                                      {getTrendProcent(item)}
                                    </span>
                                    {getTrendProcent(item) !== "" ? (
                                      checkTrend(item) ? (
                                        <NorthEastSharpIcon
                                          className={
                                            "high_arrow sm-display-none"
                                          }
                                        ></NorthEastSharpIcon>
                                      ) : (
                                        <SouthEastSharpIcon
                                          className={
                                            "low_arrow sm-display-none"
                                          }
                                        ></SouthEastSharpIcon>
                                      )
                                    ) : (
                                      ""
                                    )}
                                  </span>
                                  <span
                                    data-tooltip-id="procent_mobile_tooltip"
                                    className={"text-[9px]"}
                                  >
                                    Floor price{" "}
                                    <span
                                      className={
                                        checkTrend(item)
                                          ? "text-[#2e7d32] sm-display-inline arrow-display-none"
                                          : "text-[#d32f2f] sm-display-inline arrow-display-none"
                                      }
                                    >
                                      {getTrendProcent(item)}
                                    </span>
                                    {getTrendProcent(item) !== "" ? (
                                      checkTrend(item) ? (
                                        <NorthEastSharpIcon
                                          className={
                                            "high_arrow sm-display-inline arrow-display-none"
                                          }
                                        ></NorthEastSharpIcon>
                                      ) : (
                                        <SouthEastSharpIcon
                                          className={
                                            "low_arrow sm-display-inline arrow-display-none"
                                          }
                                        ></SouthEastSharpIcon>
                                      )
                                    ) : (
                                      ""
                                    )}
                                  </span>
                                </div>
                                <ReactTooltip
                                  id="procent_tooltip"
                                  place="bottom"
                                  content="One-Week Price Deviation"
                                  delayShow={0}
                                />
                                <ReactTooltip
                                  id="procent_mobile_tooltip"
                                  place="bottom"
                                  content="One-Week Price Deviation"
                                  delayShow={0}
                                  openOnClick
                                />
                                <div className={"flex flex-col justify-end"}>
                                  <span
                                    className={
                                      checkTrend(item)
                                        ? "text-[11px] font-[800] text-[#2e7d32] leading-[8px] text-right"
                                        : "text-[11px] font-[800] text-[#d32f2f] leading-[8px] text-right"
                                    }
                                  >
                                    {getPriceWithCurrency(
                                      Math.round(item.volume * 100) / 100,
                                      item.floor_price_symbol,
                                    )}
                                  </span>
                                  <span className={"text-[9px] text-right"}>
                                    Total Volume
                                  </span>
                                </div>
                              </div>
                              <div className={"flex justify-between mt-[2px]"}>
                                <div className={"flex flex-col justify-end"}>
                                  <span
                                    className={
                                      checkTrend(item)
                                        ? "text-[11px] font-[800] text-[#2e7d32] leading-[8px]"
                                        : "text-[11px] font-[800] text-[#d32f2f] leading-[8px]"
                                    }
                                  >
                                    {item.contracts_chain == "berachain"
                                      ? item.num_owners
                                      : item.num_owners +
                                        " (" +
                                        (
                                          (item.num_owners * 100) /
                                          item.total_supply
                                        ).toFixed(0) +
                                        "%)"}
                                  </span>
                                  <span className={"text-[9px]"}>Owners</span>
                                </div>
                                <div className={"flex flex-col justify-end"}>
                                  <span
                                    className={
                                      checkTrend(item)
                                        ? "text-[11px] font-[800] text-[#2e7d32] leading-[8px] text-right"
                                        : "text-[11px] font-[800] text-[#d32f2f] leading-[8px] text-right"
                                    }
                                  >
                                    {getPriceWithCurrency(
                                      Math.round(item.market_cap * 100) / 100,
                                      item.floor_price_symbol,
                                    )}
                                  </span>
                                  <span className={"text-[9px] text-right"}>
                                    Market Cap
                                  </span>
                                </div>
                              </div>
                            </div>
                          </figcaption>
                        </figure>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
        {selectedItem && (
          <Dialog open={dialogOpen} fullWidth={true} maxWidth={"md"}>
            <DialogTitle
              style={{ borderBottom: "1px solid rgba(33, 158, 188, 0.15)" }}
              className="flex justify-between items-center !bg-[#10243A] !text-[#E6F1FF]"
            >
              {selectedItem?.name}
              <button
                onClick={() => handleClose()}
                className="text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            </DialogTitle>
            <DialogContent className="p-0 bg-[#0b1220]">
              <div>
                {/* messages */}
                <div
                  style={{
                    padding: 20,
                    height: 600,
                    overflowY: "auto",
                    background: "#0B1220",
                  }}
                >
                  {multiAgents.map((agent, index) => (
                    <div
                      key={crypto.randomUUID()}
                      style={{
                        marginBottom: 20,
                      }}
                    >
                      <div
                        onClick={() => toggleCollapse(agent.id)}
                        className={`agent-card mb-[5px] agent-${agent.type}-header`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 18,
                          }}
                        >
                          {agent.completed ? (
                            <svg
                              className={`agent-${agent.type}-svg`}
                              width="20px"
                              height="20px"
                              viewBox="0 0 96 96"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <title />
                              <g>
                                <path d="M58.3945,32.1563,42.9961,50.625l-5.3906-6.4629a5.995,5.995,0,1,0-9.211,7.6758l9.9961,12a5.9914,5.9914,0,0,0,9.211.0059l20.0039-24a5.9988,5.9988,0,1,0-9.211-7.6875Z" />
                                <path d="M48,0A48,48,0,1,0,96,48,48.0512,48.0512,0,0,0,48,0Zm0,84A36,36,0,1,1,84,48,36.0393,36.0393,0,0,1,48,84Z" />
                              </g>
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className={`w-5 h-5 think-${agent.type}-svg animate-ai-think`}
                            >
                              <path d="M21 12a9 9 0 1 1-6.21-8.58" />
                            </svg>
                          )}
                        </div>

                        {/* title */}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontWeight: 600,
                            flex: 1,
                          }}
                        >
                          <div>{agent.title}</div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}
                          >
                            {
                              agent.subtitle?.split('[@@@]').filter(item => item.trim() !== '').map((item, index) => (
                                  <div className="runtime-item" key={index}>
                                    <span className="runtime-dot" />
                                    <span>{item}</span>
                                  </div>  
                                ) 
                              )                         
                            }
                          </div>
                        </div>

                        {/* arrow */}
                        <div key={crypto.randomUUID()}>
                          {agent.collapsed ? (
                            <i
                              className="fas fa-angle-right"
                              aria-hidden="true"
                            ></i>
                          ) : (
                            <i
                              className="fas fa-angle-down"
                              aria-hidden="true"
                            ></i>
                          )}
                        </div>
                      </div>
                      {!agent.collapsed && (
                        <div className={`agent-report agent-${agent.type} p-0`}>
                          {agent.streaming ? (
                            <div
                              key={index}
                              className="text-slate-300 font-medium p-[14px]"
                              style={{
                                minHeight: 100,
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {StringToHtml(agent.thinkingBuffer, agent.type)}
                            </div>
                          ) : (
                            ""
                          )}

                          {agent.messages.map((message, index) => {
                            if (message.role === "awaiting") {
                              return (
                                <div
                                  className="p-[14px]"
                                  key={crypto.randomUUID()}
                                >
                                  <div
                                    style={{
                                      fontWeight: "bold",
                                      marginBottom: 14,
                                    }}
                                  >
                                    {message.content}
                                  </div>

                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 12,
                                    }}
                                  >
                                    <button
                                      className={`btn-${agent.type}`}
                                      onClick={() => approve(agent.type)}
                                    >
                                      Activate
                                    </button>

                                    <button
                                      className={`btn-skip-${agent.type}`}
                                      onClick={() => reject(agent.type)}
                                      style={{
                                        cursor: "pointer",
                                      }}
                                    >
                                      Skip
                                    </button>
                                  </div>
                                </div>
                              );
                            }

                            if (message.role === "assistant") {
                              return (
                                <div
                                  className="agent-assistant-message"
                                  key={crypto.randomUUID()}
                                >
                                  {message.type === "whale" ? (
                                    <WhaleAgentComponent
                                      data={JSON.parse(message.content)}
                                    />
                                  ) : message.type === "risk" ? (
                                    <RiskAgentComponent
                                      data={JSON.parse(message.content)}
                                    />
                                  ) : (
                                    <MarketAgentComponent
                                      data={JSON.parse(message.content)}
                                    />
                                  )}
                                </div>
                              );
                            } else {
                              return (
                                <div
                                  className="agent-user-message  p-[14px]"
                                  key={crypto.randomUUID()}
                                >
                                  <ReactMarkdown>
                                    {message.content}
                                  </ReactMarkdown>
                                </div>
                              );
                            }
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
            <DialogActions
              className="bg-[#0F1726]"
              style={{ border: "1px solid rgba(255,255,255,0.05)" }}
            >
              {selectedItem?.contracts_chain == "berachain" ? (
                <a
                  href={"#"}
                  title={"Kingdomly"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick(
                      selectedItem.collection,
                      "kingdomly",
                      "https://marketplace.kingdomly.app/collection/bartio/" +
                        selectedItem.collection,
                    );
                  }}
                >
                  <img
                    width={"36px"}
                    src="/images/kingdomly.png"
                    alt="kingdomly"
                  />
                </a>
              ) : (
                <a
                  href={"#"}
                  title={"OpenSea"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick(
                      selectedItem.collection,
                      "opensea",
                      "https://opensea.io/collection/" +
                        selectedItem.collection,
                    );
                  }}
                >
                  <img width={"36px"} src="/images/opensea.png" alt="Opensea" />
                </a>
              )}
              {selectedItem?.twitter_url !== "" && (
                <a
                  href={"#"}
                  title={"Twitter"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick(
                      selectedItem.collection,
                      "twitter",
                      "https://x.com/" + selectedItem.twitter_url,
                    );
                  }}
                >
                  <img width={"36px"} src="/images/twitter.png" alt="Twitter" />
                </a>
              )}

              {selectedItem?.discord_url !== "" && (
                <a
                  href={"#"}
                  title={"Discord"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick(
                      selectedItem.collection,
                      "discord",
                      selectedItem.discord_url,
                    );
                  }}
                >
                  <img width={"36px"} src="/images/discord.png" alt="Discord" />
                </a>
              )}

              {selectedItem?.telegram_url !== "" && (
                <a
                  href={"#"}
                  title={"Telegram"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick(
                      selectedItem.collection,
                      "telegram",
                      selectedItem.telegram_url,
                    );
                  }}
                >
                  <img
                    width={"36px"}
                    src="/images/telegram.png"
                    alt="Telegram"
                  />
                </a>
              )}
            </DialogActions>
          </Dialog>
        )}
      </div>
    </>
  );
};
